
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, ConfirmDialog, Drawer, Icon, useToast } from '@kollez/design-system';
import { apiGet, apiPost, apiPatch } from '@/lib/api';
import type { Paginated } from '@/lib/tableQuery';

/**
 * /platform/plans — SubscriptionPlan catalog.
 * Renders live plans from the API. Each card links to a per-plan edit drawer (drawer TBD).
 * Compare-table below shows limits side by side.
 */

interface PlanFeatures {
  drivesAndOffers?: boolean;
  assessmentsModule?: boolean;
  nexie?: boolean;
  groupOverview?: boolean;
  nirfNaacExports?: boolean;
  sso?: boolean;
  customerSuccessManager?: boolean;
}

interface PlanLimits {
  minColleges?: number | null;
  maxColleges?: number | null;
  maxStudents?: number | null;
  aiMonthlyBudgetInr?: number;
  aiPerCollegeBudgetInr?: number;
  contractMonths?: number | null;
  supportChannels?: string[];
}

interface Plan {
  _id: string;
  code: string;
  name: string;
  price: { amount: number | null; unit: string; currency: string };
  applicableFor?: string[];
  status: 'Live' | 'Draft' | 'Retired';
  features: PlanFeatures;
  limits: PlanLimits;
}

const FEATURE_LABELS: Array<[keyof PlanFeatures, string]> = [
  ['drivesAndOffers', 'Drives and offers'],
  ['assessmentsModule', 'Assessments module'],
  ['nexie', 'Nexie'],
  ['groupOverview', 'Group overview'],
  ['nirfNaacExports', 'NIRF and NAAC exports'],
  ['sso', 'SSO'],
  ['customerSuccessManager', 'Customer success manager'],
];

function fmtColleges(l: PlanLimits): string {
  if (l.minColleges == null && l.maxColleges == null) return '—';
  if (l.minColleges === l.maxColleges) return String(l.minColleges);
  if (l.minColleges != null && l.maxColleges != null) return `${l.minColleges}–${l.maxColleges}`;
  if (l.minColleges != null) return `${l.minColleges}+`;
  return `up to ${l.maxColleges}`;
}

function fmtStudents(l: PlanLimits): string {
  return l.maxStudents == null ? 'Unlimited' : l.maxStudents.toLocaleString('en-IN');
}

function fmtAI(l: PlanLimits): string {
  if (l.aiPerCollegeBudgetInr) return `₹${l.aiPerCollegeBudgetInr.toLocaleString('en-IN')} per college`;
  return l.aiMonthlyBudgetInr ? `₹${l.aiMonthlyBudgetInr.toLocaleString('en-IN')}` : '—';
}

function fmtContract(l: PlanLimits): string {
  if (l.contractMonths == null) return 'Custom';
  if (l.contractMonths <= 6) return `${l.contractMonths} months`;
  if (l.contractMonths === 12) return '12 months';
  return `${Math.round(l.contractMonths / 12)} years`;
}

function fmtSupport(l: PlanLimits): string {
  return (l.supportChannels ?? []).join(' · ') || 'Email';
}

const LIMIT_ROWS: Array<[string, (l: PlanLimits) => string]> = [
  ['Colleges', fmtColleges],
  ['Students', fmtStudents],
  ['Nexie AI / month', fmtAI],
  ['Contract', fmtContract],
  ['Support', fmtSupport],
];

interface PlanDraft {
  _id?: string;
  code: string;
  name: string;
  description?: string;
  priceAmount: string;                // as-typed
  priceUnit: string;
  applicableFor: string;              // comma-separated
  status: 'Live' | 'Draft' | 'Retired';
  features: PlanFeatures;
  limits: {
    minColleges: string; maxColleges: string; maxStudents: string;
    aiMonthlyBudgetInr: string; aiPerCollegeBudgetInr: string;
    contractMonths: string; supportChannels: string;
  };
}
const EMPTY_DRAFT: PlanDraft = {
  code: '', name: '', description: '', priceAmount: '', priceUnit: 'per_year',
  applicableFor: '', status: 'Draft',
  features: {}, limits: { minColleges: '', maxColleges: '', maxStudents: '', aiMonthlyBudgetInr: '', aiPerCollegeBudgetInr: '', contractMonths: '', supportChannels: 'email' },
};

function planToDraft(p: Plan): PlanDraft {
  return {
    _id: p._id, code: p.code, name: p.name,
    priceAmount: p.price.amount == null ? '' : String(p.price.amount),
    priceUnit: p.price.unit ?? 'per_year',
    applicableFor: (p.applicableFor ?? []).join(', '),
    status: p.status, features: { ...p.features },
    limits: {
      minColleges: p.limits.minColleges == null ? '' : String(p.limits.minColleges),
      maxColleges: p.limits.maxColleges == null ? '' : String(p.limits.maxColleges),
      maxStudents: p.limits.maxStudents == null ? '' : String(p.limits.maxStudents),
      aiMonthlyBudgetInr: p.limits.aiMonthlyBudgetInr == null ? '' : String(p.limits.aiMonthlyBudgetInr),
      aiPerCollegeBudgetInr: p.limits.aiPerCollegeBudgetInr == null ? '' : String(p.limits.aiPerCollegeBudgetInr),
      contractMonths: p.limits.contractMonths == null ? '' : String(p.limits.contractMonths),
      supportChannels: (p.limits.supportChannels ?? []).join(', '),
    },
  };
}

function draftToPayload(d: PlanDraft) {
  const n = (s: string) => s.trim() === '' ? null : Number(s);
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  return {
    code: d.code.trim(), name: d.name.trim(),
    description: d.description || undefined,
    price: { amount: n(d.priceAmount), unit: d.priceUnit, currency: 'INR' },
    applicableFor: list(d.applicableFor),
    status: d.status, features: d.features,
    limits: {
      minColleges: n(d.limits.minColleges),
      maxColleges: n(d.limits.maxColleges),
      maxStudents: n(d.limits.maxStudents),
      aiMonthlyBudgetInr: d.limits.aiMonthlyBudgetInr ? Number(d.limits.aiMonthlyBudgetInr) : undefined,
      aiPerCollegeBudgetInr: d.limits.aiPerCollegeBudgetInr ? Number(d.limits.aiPerCollegeBudgetInr) : undefined,
      contractMonths: n(d.limits.contractMonths),
      supportChannels: list(d.limits.supportChannels),
    },
  };
}

export function PlatformPlansPage() {
  const query = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => apiGet<Paginated<Plan>>('/platform/plans?limit=25&sort=price.amount&order=asc'),
  });
  const qc = useQueryClient();
  const toast = useToast();
  const [drawer, setDrawer] = useState<PlanDraft | null>(null);
  const [retireId, setRetireId] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (d: PlanDraft) => apiPost('/platform/plans', draftToPayload(d)),
    onSuccess: () => { toast.show({ message: 'Plan created.' }); qc.invalidateQueries({ queryKey: ['platform', 'plans'] }); setDrawer(null); },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });
  const update = useMutation({
    mutationFn: (d: PlanDraft) => apiPatch(`/platform/plans/${d._id}`, draftToPayload(d)),
    onSuccess: () => { toast.show({ message: 'Plan updated.' }); qc.invalidateQueries({ queryKey: ['platform', 'plans'] }); setDrawer(null); },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });
  const retire = useMutation({
    mutationFn: (id: string) => apiPost(`/platform/plans/${id}/retire`, {}),
    onSuccess: () => { toast.show({ message: 'Plan retired.' }); qc.invalidateQueries({ queryKey: ['platform', 'plans'] }); setRetireId(null); },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });

  return (
    <div className="stack">
      <style>{`
        .plan { display: grid; gap: 10px; padding: 18px; align-content: start; }
        .plan .price { font-family: "Newsreader", Georgia, serif; font-size: 2rem; font-weight: 500; line-height: 1; }
        .feat { display: flex; gap: 8px; font-size: .875rem; }
      `}</style>
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Plans</h1>
          <p className="subhead">What each plan includes and costs. Changing a live plan affects new contracts only; existing tenants keep their terms until renewal.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setDrawer({ ...EMPTY_DRAFT })}><Icon name="plus" size={14} />New plan</button>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={!!(query.data && query.data.rows.length === 0)}
        emptyTitle="No plans in the catalog yet"
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {query.data?.rows.map((p) => {
            const activeFeatures = FEATURE_LABELS.filter(([k]) => p.features[k]);
            const priceLabel = p.price.amount == null ? 'Custom' : `₹${p.price.amount.toLocaleString('en-IN')}`;
            return (
              <div key={p._id} className="card plan">
                <div className="flex items-center justify-between">
                  <h2 className="h-section">
                    <Link to={`/platform/plans/${p._id}`} className="link" style={{ color: 'inherit' }}>{p.name}</Link>
                  </h2>
                  <span className={`badge ${p.status === 'Live' ? 'badge-success' : 'badge-outline'}`}>{p.status}</span>
                </div>
                <div>
                  <span className="price">{priceLabel}</span>
                  <div className="helper">{p.price.unit.replace(/_/g, ' ')}</div>
                </div>
                <div className="grid gap-1.5">
                  {activeFeatures.map(([k, label]) => (
                    <div key={String(k)} className="feat">
                      <Icon name="check" size={12} className="text-success mt-0.5 shrink-0" />
                      {label}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 pt-3 border-t border-line-tertiary">
                  <span className="cell-sub">{p.applicableFor?.join(', ') ?? p.code}</span>
                  <div className="flex gap-2">
                    {p.status !== 'Retired' && (
                      <button className="btn btn-ghost btn-sm text-warning" onClick={() => setRetireId(p._id)}>Retire</button>
                    )}
                    <Link className="btn btn-outline btn-sm" to={`/platform/plans/${p._id}`}>View</Link>
                    <Link className="btn btn-outline btn-sm" to={`/platform/plans/${p._id}/edit`}>Edit</Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {query.data && query.data.rows.length > 0 && (
          <section className="card">
            <div className="card-head"><h2 className="h-section">Limits by plan</h2></div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th></th>
                    {query.data.rows.map((p) => <th key={p._id}>{p.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {LIMIT_ROWS.map(([label, fn]) => (
                    <tr key={label}>
                      <td className="cell-title">{label}</td>
                      {query.data!.rows.map((p) => <td key={p._id}>{fn(p.limits)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </AsyncBoundary>

      <Drawer open={!!drawer} onClose={() => setDrawer(null)} title={drawer?._id ? `Edit plan · ${drawer.name || drawer.code}` : 'New plan'} size="lg">
        {drawer && (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1.5">
                <span className="label">Code</span>
                <input className="input" value={drawer.code} disabled={!!drawer._id} onChange={(e) => setDrawer({ ...drawer, code: e.target.value })} placeholder="e.g. college" />
              </label>
              <label className="grid gap-1.5">
                <span className="label">Name</span>
                <input className="input" value={drawer.name} onChange={(e) => setDrawer({ ...drawer, name: e.target.value })} placeholder="College plan" />
              </label>
            </div>
            <label className="grid gap-1.5">
              <span className="label">Description</span>
              <textarea className="textarea" rows={2} value={drawer.description ?? ''} onChange={(e) => setDrawer({ ...drawer, description: e.target.value })} />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="grid gap-1.5">
                <span className="label">Price (₹) — empty for Custom</span>
                <input className="input" value={drawer.priceAmount} onChange={(e) => setDrawer({ ...drawer, priceAmount: e.target.value })} />
              </label>
              <label className="grid gap-1.5">
                <span className="label">Unit</span>
                <select className="select" value={drawer.priceUnit} onChange={(e) => setDrawer({ ...drawer, priceUnit: e.target.value })}>
                  <option value="per_year">per year</option>
                  <option value="per_college_per_year">per college per year</option>
                  <option value="custom">custom</option>
                </select>
              </label>
              <label className="grid gap-1.5">
                <span className="label">Status</span>
                <select className="select" value={drawer.status} onChange={(e) => setDrawer({ ...drawer, status: e.target.value as PlanDraft['status'] })}>
                  <option value="Draft">Draft</option>
                  <option value="Live">Live</option>
                  <option value="Retired">Retired</option>
                </select>
              </label>
            </div>
            <label className="grid gap-1.5">
              <span className="label">Applicable for (comma-separated tenant kinds)</span>
              <input className="input" value={drawer.applicableFor} onChange={(e) => setDrawer({ ...drawer, applicableFor: e.target.value })} placeholder="COLLEGE, GROUP, UNIVERSITY" />
            </label>

            <fieldset className="grid gap-2 well p-4">
              <legend className="text-sm font-medium">Features included</legend>
              {FEATURE_LABELS.map(([k, label]) => (
                <label key={String(k)} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!drawer.features[k]} onChange={(e) => setDrawer({ ...drawer, features: { ...drawer.features, [k]: e.target.checked } })} />
                  {label}
                </label>
              ))}
            </fieldset>

            <fieldset className="grid grid-cols-2 gap-3 well p-4">
              <legend className="text-sm font-medium">Limits</legend>
              <label className="grid gap-1.5"><span className="label">Min colleges</span><input className="input" value={drawer.limits.minColleges} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, minColleges: e.target.value } })} /></label>
              <label className="grid gap-1.5"><span className="label">Max colleges</span><input className="input" value={drawer.limits.maxColleges} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, maxColleges: e.target.value } })} /></label>
              <label className="grid gap-1.5"><span className="label">Max students</span><input className="input" value={drawer.limits.maxStudents} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, maxStudents: e.target.value } })} /></label>
              <label className="grid gap-1.5"><span className="label">Contract months</span><input className="input" value={drawer.limits.contractMonths} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, contractMonths: e.target.value } })} /></label>
              <label className="grid gap-1.5"><span className="label">Nexie AI ₹ / month</span><input className="input" value={drawer.limits.aiMonthlyBudgetInr} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, aiMonthlyBudgetInr: e.target.value } })} /></label>
              <label className="grid gap-1.5"><span className="label">Nexie AI ₹ / college / month</span><input className="input" value={drawer.limits.aiPerCollegeBudgetInr} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, aiPerCollegeBudgetInr: e.target.value } })} /></label>
              <label className="grid gap-1.5 col-span-2"><span className="label">Support channels (comma-separated)</span><input className="input" value={drawer.limits.supportChannels} onChange={(e) => setDrawer({ ...drawer, limits: { ...drawer.limits, supportChannels: e.target.value } })} placeholder="email, phone, whatsapp" /></label>
            </fieldset>
          </div>
        )}
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setDrawer(null)}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!drawer || !drawer.code.trim() || !drawer.name.trim() || create.isPending || update.isPending}
            onClick={() => drawer && (drawer._id ? update.mutate(drawer) : create.mutate(drawer))}
          >
            {create.isPending || update.isPending ? 'Saving…' : (drawer?._id ? 'Save changes' : 'Create plan')}
          </button>
        </div>
      </Drawer>

      <ConfirmDialog
        open={!!retireId}
        onClose={() => setRetireId(null)}
        title="Retire this plan?"
        body="It stops showing in the tenant provisioning picker. Existing subscriptions keep working — their planSnapshot is frozen."
        cta="Retire"
        tone="danger"
        onConfirm={() => { if (retireId) retire.mutate(retireId); }}
      />
    </div>
  );
}
